import moment from "moment";
import { useIntl } from "react-intl";
import { useT } from "@hooks";
import type { BatchDeleteProcessState } from "./useBatchDeleteProcess";
import { formatCurrency } from "./batchDeleteUtils";

const toPercentage = (processed: number, total: number) =>
  total > 0 ? Math.min(100, Math.max(0, Math.round((processed / total) * 100))) : 0;

interface ProgressProps {
  state: BatchDeleteProcessState;
}

export const BatchDeleteProgress = ({ state }: ProgressProps) => {
  const t = useT();
  const { locale } = useIntl();
  const processed = state.batch?.summary?.processed ?? 0;
  const requested = state.batch?.summary?.requested ?? 0;
  const percentage = toPercentage(processed, requested);

  return (
    <div className="width-100pct" data-testid="batch-delete-progress">
      <div className="display-flex justify-content-between margin-bottom-5">
        <span className="text-color-dark">{t("batchDelete.progress.processing")}</span>
        <span className="text-color-primary text-bold">{percentage}%</span>
      </div>
      <div className="progress" style={{ height: "20px" }}>
        <div
          className="progress-bar progress-bar-primary"
          role="progressbar"
          style={{ width: `${percentage}%` }}
          aria-valuenow={percentage}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
      <div className="text-center margin-top-5 text-color-gray">
        {t("batchDelete.progress.items", {
          processed: processed.toLocaleString(locale),
          total: requested.toLocaleString(locale),
        })}
      </div>
    </div>
  );
};

interface NotDeletedListProps {
  state: BatchDeleteProcessState;
  onLoadMore: () => void;
}

export const BatchDeleteNotDeletedList = ({ state, onLoadMore }: NotDeletedListProps) => {
  const t = useT();

  if (state.notDeletedItems.length === 0) return null;

  return (
    <div className="width-100pct" data-testid="batch-delete-not-deleted">
      <p className="margin-top-0 text-medium">{t("batchDelete.result.notDeletedTitle")}</p>
      <div className="max-height-300 overflow-y-auto">
        <table className="table table-striped table-bordered margin-0">
          <thead>
            <tr>
              <th>{t("table.column.date")}</th>
              <th>{t("table.column.category")}</th>
              <th>{t("table.column.amount")}</th>
              <th>{t("batchDelete.result.reason")}</th>
            </tr>
          </thead>
          <tbody>
            {state.notDeletedItems.map((item) => (
              <tr key={item.transactionId}>
                <td>{item.transactionDate ? moment(item.transactionDate).format("DD/MM/YYYY") : "-"}</td>
                <td>{item.categoryName ?? "-"}</td>
                <td>{item.amount === null || item.amount === undefined ? "-" : formatCurrency(item.amount)}</td>
                <td>{item.message ?? "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {state.hasMoreNotDeleted && (
        <div className="text-center margin-top-10">
          <button
            type="button"
            className="btn btn-default btn-sm rounded-small"
            disabled={state.isLoadingNotDeleted}
            onClick={onLoadMore}
          >
            {t("batchDelete.result.loadMore")}
          </button>
        </div>
      )}
    </div>
  );
};
